import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FileDown, FileSpreadsheet, Gift, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns-tz";

import PageHeader from "../../../components/PageHeader";
import { SELECT_STYLES } from "../../../components/DateFilters/constants";
import { BrazilDate } from "../../../utils/BrazilDate";
import { getErrorMessage } from "../../../utils/errorHandler";
import RelatorioPeriodoMaxHint from "../components/RelatorioPeriodoMaxHint";
import RelatorioQueryErrorAlert from "../components/RelatorioQueryErrorAlert";
import { warnIfReportPeriodInvalid } from "../utils/reportPeriodValidation";
import { lojaService } from "../../../services/lojaService";
import {
  BRINDE_CATEGORIAS,
  filaPizzasService,
  RELATORIO_BRINDE_PAGE_SIZE,
  type BrindeCategoria,
} from "../../../services/filaPizzasService";
import { tamanhoService } from "../../../services/tamanhoService";
import RelatorioSaboresTablePagination from "../components/RelatorioSaboresTablePagination";
import {
  exportRelatorioBrindePdf,
  exportRelatorioBrindeXlsx,
  RelatorioBrindeFiltroResumo,
} from "../utils/relatorioBrindeExport";

type LojaOpt = { value: number; label: string };
type TamanhoOpt = { value: number; label: string };
type CategoriaOpt = { value: BrindeCategoria; label: string };

const LOJA_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  LojaOpt,
  false
>;
const TAMANHO_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  TamanhoOpt,
  false
>;
const CATEGORIA_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  CategoriaOpt,
  false
>;

const CATEGORIA_OPTIONS: CategoriaOpt[] = BRINDE_CATEGORIAS.map((c) => ({
  value: c,
  label: c,
}));

function getDefaultMonthRange() {
  const now = new Date();
  const tz = BrazilDate.getTimezone();
  const yearStr = format(now, "yyyy", { timeZone: tz });
  const monthStr = format(now, "MM", { timeZone: tz });
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const firstDay = BrazilDate.create(year, month, 1);
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const firstDayNextMonth = BrazilDate.create(nextYear, nextMonth, 1);
  const lastDay = new Date(firstDayNextMonth.getTime() - 24 * 60 * 60 * 1000);
  return {
    dataInicio: BrazilDate.formatISO(firstDay),
    dataFim: BrazilDate.formatISO(lastDay),
  };
}

const DEFAULT_MONTH_FILTERS = getDefaultMonthRange();

function formatQty(n: number): string {
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

const PizzasBrinde: React.FC = () => {
  const [startDate, setStartDate] = useState(DEFAULT_MONTH_FILTERS.dataInicio);
  const [endDate, setEndDate] = useState(DEFAULT_MONTH_FILTERS.dataFim);
  const [lojaId, setLojaId] = useState<number | null>(null);
  const [tamanhoId, setTamanhoId] = useState<number | null>(null);
  const [motivoQ, setMotivoQ] = useState("");
  const [saborQ, setSaborQ] = useState("");
  const [categoria, setCategoria] = useState<BrindeCategoria | null>(null);
  const [page, setPage] = useState(1);
  const [applied, setApplied] = useState<null | {
    dataInicio: string;
    dataFim: string;
    lojaId: number | null;
    tamanhoId: number | null;
    categoria: BrindeCategoria | null;
    motivoContains: string;
    saborContains: string;
  }>(null);

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const lojaOptions = useMemo(
    () => lojas.map((l) => ({ value: l.id, label: l.nome })),
    [lojas]
  );
  const lojaSel = lojaOptions.find((o) => o.value === (lojaId ?? -1)) ?? null;

  const { data: tamanhos = [], isFetching: tamanhosCarregando } = useQuery({
    queryKey: ["tamanhos-relatorio-brinde", lojaId],
    queryFn: () => tamanhoService.getTamanhos({ lojaId: lojaId as number }),
    enabled: typeof lojaId === "number" && lojaId > 0,
  });

  const { data: tamanhosAppliedLabel = [] } = useQuery({
    queryKey: [
      "tamanhos-relatorio-brinde-applied-label",
      applied?.lojaId ?? null,
    ],
    queryFn: () =>
      tamanhoService.getTamanhos({ lojaId: applied?.lojaId as number }),
    enabled: typeof applied?.lojaId === "number" && applied.lojaId > 0,
  });

  const tamanhoOptions = useMemo(
    () =>
      tamanhos.map((t) => ({
        value: t.id,
        label: t.nome,
      })),
    [tamanhos]
  );

  const tamanhoSel =
    tamanhoOptions.find((o) => o.value === (tamanhoId ?? -1)) ?? null;

  const categoriaSel =
    categoria != null
      ? (CATEGORIA_OPTIONS.find((o) => o.value === categoria) ?? null)
      : null;

  useEffect(() => {
    setTamanhoId(null);
  }, [lojaId]);

  useEffect(() => {
    setPage(1);
  }, [applied]);

  const commonFilters = useMemo(
    () => ({
      dataInicio: applied?.dataInicio ?? "",
      dataFim: applied?.dataFim ?? "",
      lojaId: applied?.lojaId ?? null,
      tamanhoId: applied?.tamanhoId ?? null,
      categoria: applied?.categoria ?? null,
      motivoContains: applied?.motivoContains || undefined,
      saborContains: applied?.saborContains || undefined,
    }),
    [applied]
  );

  const {
    data: linhas = [],
    isFetching: isFetchingExport,
    isError: isExportError,
    error: exportError,
  } = useQuery({
    queryKey: ["relatorio-brinde-pizzas", applied ?? "no-applied"],
    enabled: applied != null,
    queryFn: () => filaPizzasService.getRelatorioBrindePizzas(commonFilters),
  });

  const { data: paged, isFetching, isError, error } = useQuery({
    queryKey: [
      "relatorio-brinde-pizzas-paginado",
      applied ?? "no-applied",
      page,
      RELATORIO_BRINDE_PAGE_SIZE,
    ],
    enabled: applied != null,
    queryFn: () =>
      filaPizzasService.getRelatorioBrindePizzasPaginated(
        commonFilters,
        page,
        RELATORIO_BRINDE_PAGE_SIZE
      ),
    placeholderData: (prev) => prev,
  });

  const totalFrac = useMemo(() => paged?.totalQuantidadeGeral ?? 0, [paged]);
  const linhasPagina = paged?.data ?? [];
  const totalPages = paged?.totalPages ?? 1;

  const filtrosPdf = useMemo((): RelatorioBrindeFiltroResumo[] => {
    if (!applied) {
      return [
        { label: "Loja", value: "—" },
        { label: "Tamanho", value: "—" },
        { label: "Categoria", value: "Todas" },
        { label: "Motivo (contém)", value: "Qualquer" },
        { label: "Sabor (contém)", value: "Qualquer" },
      ];
    }
    const lojaLabel =
      applied.lojaId != null
        ? (lojaOptions.find((o) => o.value === applied.lojaId)?.label ??
          `ID ${applied.lojaId}`)
        : "Todas";
    const tamLabel =
      applied.lojaId == null || applied.tamanhoId == null
        ? "Todos"
        : (tamanhosAppliedLabel.find((t) => t.id === applied.tamanhoId)?.nome ??
          `ID ${applied.tamanhoId}`);
    return [
      { label: "Loja", value: lojaLabel },
      { label: "Tamanho", value: tamLabel },
      {
        label: "Categoria",
        value: applied.categoria ?? "Todas",
      },
      {
        label: "Motivo (contém)",
        value: applied.motivoContains?.trim()
          ? applied.motivoContains.trim()
          : "Qualquer",
      },
      {
        label: "Sabor (contém)",
        value: applied.saborContains?.trim()
          ? applied.saborContains.trim()
          : "Qualquer",
      },
    ];
  }, [applied, lojaOptions, tamanhosAppliedLabel]);

  const buildExportInput = useCallback(() => {
    if (!applied) return null;
    return {
      periodoInicioISO: applied.dataInicio,
      periodoFimISO: applied.dataFim,
      filtros: filtrosPdf,
      linhas: linhas.map((r) => ({
        lojaNome: r.lojaNome,
        tamanhoNome: r.tamanhoNome,
        saborNome: r.saborNome,
        motivo: r.motivo,
        categoria: r.categoria,
        quantidade: r.quantidade,
      })),
      totalQuantidadeFracao: totalFrac,
      geradoEmLabel: BrazilDate.formatBR(new Date(), true),
    };
  }, [linhas, applied, filtrosPdf, totalFrac]);

  const handleExportPdf = useCallback(() => {
    if (!applied) {
      toast.warning("Aplique os filtros antes de exportar.");
      return;
    }
    if (linhas.length === 0) {
      toast.warning("Não há linhas para exportar.");
      return;
    }
    try {
      const input = buildExportInput();
      if (!input) {
        toast.warning("Aplique os filtros antes de exportar.");
        return;
      }
      exportRelatorioBrindePdf(input);
      toast.success("PDF baixado.");
    } catch {
      toast.error("Não foi possível gerar o PDF.");
    }
  }, [applied, linhas.length, buildExportInput]);

  const handleExportExcel = useCallback(() => {
    if (!applied) {
      toast.warning("Aplique os filtros antes de exportar.");
      return;
    }
    if (linhas.length === 0) {
      toast.warning("Não há linhas para exportar.");
      return;
    }
    try {
      const input = buildExportInput();
      if (!input) {
        toast.warning("Aplique os filtros antes de exportar.");
        return;
      }
      exportRelatorioBrindeXlsx(input);
      toast.success("Planilha Excel baixada.");
    } catch {
      toast.error("Não foi possível gerar o Excel.");
    }
  }, [applied, linhas.length, buildExportInput]);

  const handleAplicar = () => {
    if (warnIfReportPeriodInvalid(startDate, endDate)) return;

    setApplied({
      dataInicio: startDate,
      dataFim: endDate,
      lojaId,
      tamanhoId: lojaId != null ? tamanhoId : null,
      categoria,
      motivoContains: motivoQ.trim(),
      saborContains: saborQ.trim(),
    });
  };

  const queryErrorMessage =
    isError || isExportError
      ? getErrorMessage(error ?? exportError)
      : null;

  const pIni =
    applied != null
      ? BrazilDate.formatBR(BrazilDate.fromISOString(applied.dataInicio), false)
      : null;
  const pFim =
    applied != null
      ? BrazilDate.formatBR(BrazilDate.fromISOString(applied.dataFim), false)
      : null;

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader title="Pizzas Brinde" description="" />

      <div className="mb-6 rounded-lg border border-neutral-200 bg-neutral-50/80 p-4">
        <p className="mb-3 text-sm font-medium text-neutral-800">Filtros</p>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="br-ini"
              className="text-xs font-medium text-neutral-700"
            >
              Data início
            </label>
            <input
              id="br-ini"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="br-fim"
              className="text-xs font-medium text-neutral-700"
            >
              Data fim
            </label>
            <input
              id="br-fim"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-neutral-700">Loja</span>
            <ReactSelect<LojaOpt, false>
              inputId="br-loja"
              value={lojaSel}
              onChange={(o: SingleValue<LojaOpt>) =>
                setLojaId(o?.value ?? null)
              }
              options={lojaOptions}
              isClearable
              placeholder="Todas"
              styles={LOJA_SELECT_STYLES}
              className="text-sm"
              noOptionsMessage={() => "Nenhuma loja"}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-neutral-700">
              Tamanho
            </span>
            <ReactSelect<TamanhoOpt, false>
              inputId="br-tamanho"
              value={tamanhoSel}
              onChange={(o: SingleValue<TamanhoOpt>) =>
                setTamanhoId(o?.value ?? null)
              }
              options={tamanhoOptions}
              isClearable
              isDisabled={lojaId == null}
              isLoading={lojaId != null && tamanhosCarregando}
              placeholder={
                lojaId == null
                  ? "Escolha a loja para listar tamanhos"
                  : "Todos os tamanhos"
              }
              styles={TAMANHO_SELECT_STYLES}
              className="text-sm"
              noOptionsMessage={() =>
                lojaId == null
                  ? "Selecione a loja"
                  : "Nenhum tamanho cadastrado"
              }
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-neutral-700">
              Categoria
            </span>
            <ReactSelect<CategoriaOpt, false>
              inputId="br-categoria"
              value={categoriaSel}
              onChange={(o: SingleValue<CategoriaOpt>) =>
                setCategoria(o?.value ?? null)
              }
              options={CATEGORIA_OPTIONS}
              isClearable
              placeholder="Todas"
              styles={CATEGORIA_SELECT_STYLES}
              className="text-sm"
              noOptionsMessage={() => "Nenhuma categoria"}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="br-motivo"
              className="text-xs font-medium text-neutral-700"
            >
              Motivo contém
            </label>
            <input
              id="br-motivo"
              type="text"
              value={motivoQ}
              onChange={(e) => setMotivoQ(e.target.value)}
              placeholder="Ex.: Marketing"
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="br-sabor"
              className="text-xs font-medium text-neutral-700"
            >
              Sabor contém
            </label>
            <input
              id="br-sabor"
              type="text"
              value={saborQ}
              onChange={(e) => setSaborQ(e.target.value)}
              placeholder="Ex.: Calabresa"
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
        <RelatorioPeriodoMaxHint />
        <button
          type="button"
          onClick={handleAplicar}
          className="mt-4 inline-flex rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
        >
          Aplicar filtros
        </button>
      </div>

      {applied != null ? (
        <section className="rounded-2xl border border-neutral-200/90 bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 flex-wrap items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Gift size={22} strokeWidth={2} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base font-semibold text-neutral-900">
                  Totais por loja, tamanho, sabor, categoria e motivo
                </h2>
                <p className="mt-1 text-sm text-neutral-600">
                  Período <span className="font-medium">{pIni}</span> a{" "}
                  <span className="font-medium">{pFim}</span>. Baseado em{" "}
                  <code className="rounded bg-neutral-100 px-1 text-xs">
                    fila_salgada
                  </code>{" "}
                  e{" "}
                  <code className="rounded bg-neutral-100 px-1 text-xs">
                    fila_doce
                  </code>{" "}
                  com <span className="font-medium">status done</span> e{" "}
                  <span className="font-medium">finished_at</span> no intervalo.
                </p>
                <p className="mt-2 text-xs text-neutral-500">
                  Soma das quantidades fracionadas:{" "}
                  <span className="font-semibold tabular-nums text-neutral-800">
                    {formatQty(totalFrac)}
                  </span>
                  <span className="mx-2 hidden text-neutral-300 sm:inline">
                    |
                  </span>
                  <span className="block sm:inline">
                    Nesta página:{" "}
                    <span className="font-semibold tabular-nums text-neutral-800">
                      {formatQty(paged?.totalQuantidadePagina ?? 0)}
                    </span>
                  </span>
                </p>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleExportExcel}
                disabled={!applied || linhas.length === 0 || isFetchingExport}
                className="inline-flex items-center gap-2 rounded-lg border border-emerald-600 bg-white px-3 py-2 text-sm font-semibold text-emerald-800 shadow-sm transition-opacity hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FileSpreadsheet size={18} />
                Excel
              </button>
              <button
                type="button"
                onClick={handleExportPdf}
                disabled={!applied || linhas.length === 0 || isFetchingExport}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FileDown size={18} />
                PDF (A4)
              </button>
            </div>
          </div>

          {isFetching ? (
            <div className="flex items-center justify-center gap-2 py-14 text-sm text-neutral-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              Carregando…
            </div>
          ) : queryErrorMessage ? (
            <RelatorioQueryErrorAlert message={queryErrorMessage} />
          ) : linhasPagina.length === 0 ? (
            <p className="py-10 text-center text-sm text-neutral-500">
              Nenhuma pizza brinde finalizada no período para os filtros.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto rounded-xl border border-neutral-100">
                <table className="w-full min-w-[820px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-neutral-200 bg-neutral-50/90">
                      <th className="px-3 py-2.5 font-semibold text-neutral-800 sm:px-4">
                        Loja
                      </th>
                      <th className="px-3 py-2.5 font-semibold text-neutral-800 sm:px-4">
                        Tamanho
                      </th>
                      <th className="px-3 py-2.5 font-semibold text-neutral-800 sm:px-4">
                        Sabor
                      </th>
                      <th className="px-3 py-2.5 font-semibold text-neutral-800 sm:px-4">
                        Categoria
                      </th>
                      <th className="px-3 py-2.5 font-semibold text-neutral-800 sm:px-4">
                        Motivo
                      </th>
                      <th className="px-3 py-2.5 text-right font-semibold text-neutral-800 sm:px-4">
                        Quantidade
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhasPagina.map((row, i) => (
                      <tr
                        key={`${row.lojaNome}-${row.tamanhoNome}-${row.saborNome}-${row.categoria}-${row.motivo}-${i}`}
                        className="border-b border-neutral-100 last:border-b-0"
                      >
                        <td className="px-3 py-2.5 text-neutral-900 sm:px-4">
                          {row.lojaNome}
                        </td>
                        <td className="px-3 py-2.5 text-neutral-900 sm:px-4">
                          {row.tamanhoNome}
                        </td>
                        <td className="px-3 py-2.5 text-neutral-900 sm:px-4">
                          {row.saborNome}
                        </td>
                        <td className="px-3 py-2.5 font-medium text-neutral-900 sm:px-4">
                          {row.categoria}
                        </td>
                        <td className="max-w-[220px] px-3 py-2.5 text-neutral-800 sm:px-4">
                          {row.motivo}
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium tabular-nums text-neutral-900 sm:px-4">
                          {formatQty(row.quantidade)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <RelatorioSaboresTablePagination
                page={paged?.page ?? page}
                totalPages={totalPages}
                totalRegistros={paged?.total ?? 0}
                totalGeralFmt={formatQty(paged?.totalQuantidadeGeral ?? 0)}
                totalPaginaFmt={formatQty(paged?.totalQuantidadePagina ?? 0)}
                isLoading={isFetching}
                onPrevious={() => setPage((p) => Math.max(1, p - 1))}
                onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
              />
            </>
          )}
        </section>
      ) : (
        <p className="rounded-2xl border border-neutral-200/90 bg-white p-6 text-sm text-neutral-600 shadow-sm">
          Selecione os filtros e clique em <strong>Aplicar filtros</strong>.
        </p>
      )}
    </main>
  );
};

export default PizzasBrinde;
